/**
 * Tag Quality Validator for AlgoRhythm Recommendation Engine
 *
 * This utility validates and scores tags for their effectiveness in the
 * recommendation engine. It helps ensure tags are specific, searchable,
 * and useful for matching user preferences.
 */

export interface TagQualityScore {
  score: number; // 0-100, higher is better for AlgoRhythm
  category: 'excellent' | 'good' | 'fair' | 'poor';
  feedback: string[];
  specificityScore: number;
  searchabilityScore: number;
  algorhythmValue: number;
}

export interface TagAnalysis {
  totalScore: number;
  averageScore: number;
  tagCount: number;
  qualityDistribution: {
    excellent: number;
    good: number;
    fair: number;
    poor: number;
  };
  recommendations: string[];
  missingTags: string[];
}

export class TagQualityValidator {
  // High-value tag patterns for AlgoRhythm matching
  private static HIGH_VALUE_PATTERNS = {
    // Hair-specific (critical for visual matching)
    hair: [
      /multi-color-hair/i,
      /colorful-hair/i,
      /rainbow-hair/i,
      /ombre-hair/i,
      /highlights-hair/i,
      /[a-z]+-hair-color/i,
      /[a-z]+-hair-style/i,
    ],

    // Makeup-specific (critical for style matching)
    makeup: [
      /bold-makeup/i,
      /dramatic-makeup/i,
      /natural-makeup/i,
      /heavy-makeup/i,
      /minimal-makeup/i,
      /glam-makeup/i,
      /[a-z]+-eye-makeup/i,
      /[a-z]+-lip-makeup/i,
    ],

    // Style combinations (high search value)
    styleCombo: [
      /rock-star-look/i,
      /teen-rock/i,
      /pop-star/i,
      /country-style/i,
      /hip-hop-look/i,
      /classical-style/i,
      /alternative-look/i,
      /mainstream-style/i,
    ],

    // Age + style (demographic targeting)
    ageStyle: [
      /teen-[a-z]+/i,
      /young-adult-[a-z]+/i,
      /gen-z-[a-z]+/i,
      /millennial-[a-z]+/i,
    ],

    // Performance context (use case matching)
    performance: [
      /studio-ready/i,
      /stage-performer/i,
      /concert-ready/i,
      /recording-artist/i,
      /live-performance/i,
      /music-video-ready/i,
    ],
  };

  // Generic tags that don't help with matching (low AlgoRhythm value)
  private static GENERIC_PATTERNS = [
    /^versatile$/i,
    /^contemporary$/i,
    /^modern$/i,
    /^professional$/i,
    /^adaptable$/i,
    /^charismatic$/i,
    /^elegant$/i,
    /^photogenic$/i,
    /^youthful$/i,
    /^talented$/i,
    /^performance$/i,
    /^stars$/i,
  ];

  /**
   * Score a single tag for its AlgoRhythm recommendation value
   */
  static scoreTag(tag: string): TagQualityScore {
    const feedback: string[] = [];
    let specificityScore = 0;
    let searchabilityScore = 0;
    let algorhythmValue = 0;

    // Check if tag is generic (low value)
    const isGeneric = this.GENERIC_PATTERNS.some(pattern => pattern.test(tag));
    if (isGeneric) {
      feedback.push(`"${tag}" is too generic for effective matching`);
      specificityScore = 10;
      searchabilityScore = 20;
      algorhythmValue = 15;
    } else {
      // Check for high-value patterns
      let patternFound = false;

      for (const [category, patterns] of Object.entries(
        this.HIGH_VALUE_PATTERNS
      )) {
        if (patterns.some(pattern => pattern.test(tag))) {
          patternFound = true;
          specificityScore += 25;
          searchabilityScore += 25;
          algorhythmValue += 30;
          feedback.push(`Excellent ${category} tag - high search value`);
          break;
        }
      }

      if (!patternFound) {
        // Check for compound tags (medium value)
        if (tag.includes('-') && tag.split('-').length >= 2) {
          specificityScore = 60;
          searchabilityScore = 70;
          algorhythmValue = 65;
          feedback.push('Good compound tag - searchable');
        } else {
          // Simple single-word tag (lower value)
          specificityScore = 40;
          searchabilityScore = 50;
          algorhythmValue = 45;
          feedback.push('Basic tag - could be more specific');
        }
      }
    }

    const totalScore = Math.round(
      (specificityScore + searchabilityScore + algorhythmValue) / 3
    );

    let category: TagQualityScore['category'];
    if (totalScore >= 80) category = 'excellent';
    else if (totalScore >= 60) category = 'good';
    else if (totalScore >= 40) category = 'fair';
    else category = 'poor';

    return {
      score: totalScore,
      category,
      feedback,
      specificityScore,
      searchabilityScore,
      algorhythmValue,
    };
  }

  /**
   * Analyze a complete tag set for AlgoRhythm effectiveness
   */
  static analyzeTagSet(
    tags: string[],
    metadata?: {
      hairColor?: string;
      makeupType?: string;
      musicalStyle?: string[];
      ageGroup?: string;
      archetype?: string;
    }
  ): TagAnalysis {
    const tagScores = tags.map(tag => this.scoreTag(tag));
    const totalScore = tagScores.reduce((sum, score) => sum + score.score, 0);
    const averageScore =
      tags.length > 0 ? Math.round(totalScore / tags.length) : 0;

    const qualityDistribution = {
      excellent: tagScores.filter(s => s.category === 'excellent').length,
      good: tagScores.filter(s => s.category === 'good').length,
      fair: tagScores.filter(s => s.category === 'fair').length,
      poor: tagScores.filter(s => s.category === 'poor').length,
    };

    const recommendations: string[] = [];
    const missingTags: string[] = [];

    // Generate recommendations based on metadata
    if (metadata) {
      if (metadata.hairColor?.toLowerCase().includes('multi-color')) {
        const hasHairTags = tags.some(
          tag => tag.includes('multi-color') || tag.includes('colorful-hair')
        );
        if (!hasHairTags) {
          missingTags.push('multi-color-hair', 'colorful-hair');
          recommendations.push(
            'Add specific hair color tags for better visual matching'
          );
        }
      }

      if (metadata.makeupType?.toLowerCase().includes('bold')) {
        const hasMakeupTags = tags.some(
          tag => tag.includes('bold') || tag.includes('dramatic')
        );
        if (!hasMakeupTags) {
          missingTags.push('bold-makeup', 'dramatic-makeup');
          recommendations.push('Add makeup intensity tags for style matching');
        }
      }

      if (metadata.musicalStyle?.includes('Rock')) {
        const hasGenreTags = tags.some(
          tag => tag.includes('rock') || tag.includes('alternative')
        );
        if (!hasGenreTags) {
          missingTags.push('rock-star-look', 'alternative-style');
          recommendations.push('Add musical genre + style combination tags');
        }
      }

      if (metadata.ageGroup === 'teen') {
        const hasAgeTags = tags.some(
          tag => tag.includes('teen') || tag.includes('young')
        );
        if (!hasAgeTags) {
          missingTags.push('teen-style', 'young-performer');
          recommendations.push(
            'Add age-specific style tags for demographic targeting'
          );
        }
      }
    }

    // General recommendations
    if (qualityDistribution.poor > qualityDistribution.excellent) {
      recommendations.push(
        'Replace generic tags with more specific, searchable alternatives'
      );
    }

    if (tags.length < 5) {
      recommendations.push(
        'Consider adding more specific tags for better discoverability'
      );
    }

    if (qualityDistribution.excellent === 0) {
      recommendations.push(
        'Add high-value compound tags for better AlgoRhythm matching'
      );
    }

    return {
      totalScore,
      averageScore,
      tagCount: tags.length,
      qualityDistribution,
      recommendations,
      missingTags,
    };
  }

  /**
   * Generate ideal tags based on asset metadata
   */
  static generateIdealTags(metadata: {
    hairColor?: string;
    hairStyle?: string;
    makeupType?: string;
    musicalStyle?: string[];
    ageGroup?: string;
    archetype?: string;
    gender?: string;
    eyeColor?: string;
    skinTone?: string;
  }): string[] {
    const idealTags: string[] = [];

    // Hair-based tags
    if (metadata.hairColor) {
      const color = metadata.hairColor.toLowerCase();
      if (color.includes('multi-color') || color.includes('colorful')) {
        idealTags.push(
          'multi-color-hair',
          'colorful-hair',
          'unique-hair-color'
        );
      } else if (color !== 'unknown') {
        idealTags.push(`${color}-hair`, `${color}-hair-color`);
      }
    }

    if (metadata.hairStyle) {
      const style = metadata.hairStyle.toLowerCase();
      idealTags.push(`${style}-hair`, `${style}-hairstyle`);
    }

    // Makeup-based tags
    if (metadata.makeupType) {
      const makeup = metadata.makeupType.toLowerCase();
      idealTags.push(`${makeup}-makeup`);
      if (makeup.includes('bold') || makeup.includes('dramatic')) {
        idealTags.push('statement-makeup', 'heavy-makeup');
      }
    }

    // Musical + appearance combinations
    if (metadata.musicalStyle && metadata.musicalStyle.length > 0) {
      for (const style of metadata.musicalStyle) {
        const genre = style.toLowerCase();
        idealTags.push(`${genre}-performer`, `${genre}-style`);
        if (genre === 'rock') {
          idealTags.push('rock-star-look', 'edgy-style', 'alternative-look');
        }
      }
    }

    // Age + style combinations
    if (metadata.ageGroup) {
      const age = metadata.ageGroup.toLowerCase();
      idealTags.push(`${age}-performer`, `${age}-style`);
      if (age === 'teen') {
        idealTags.push('young-performer', 'generation-z', 'youthful-style');
      }
    }

    // Performance context
    if (metadata.archetype) {
      const archetype = metadata.archetype.toLowerCase();
      idealTags.push(`${archetype}-style`);
      if (archetype === 'performer') {
        idealTags.push('stage-ready', 'performance-ready', 'concert-style');
      }
    }

    return Array.from(new Set(idealTags)); // Remove duplicates
  }

  /**
   * Compare current tags against ideal tags
   */
  static compareToIdeal(
    currentTags: string[],
    idealTags: string[]
  ): {
    matchScore: number;
    missing: string[];
    extra: string[];
    suggestions: string[];
  } {
    const currentSet = new Set(currentTags.map(t => t.toLowerCase()));
    const idealSet = new Set(idealTags.map(t => t.toLowerCase()));

    const missing = idealTags.filter(tag => !currentSet.has(tag.toLowerCase()));
    const extra = currentTags.filter(tag => !idealSet.has(tag.toLowerCase()));
    const matches = idealTags.filter(tag => currentSet.has(tag.toLowerCase()));

    const matchScore =
      idealTags.length > 0
        ? Math.round((matches.length / idealTags.length) * 100)
        : 100;

    const suggestions: string[] = [];
    if (missing.length > 0) {
      suggestions.push(
        `Add missing high-value tags: ${missing.slice(0, 3).join(', ')}`
      );
    }
    if (
      extra.filter(tag => this.GENERIC_PATTERNS.some(p => p.test(tag))).length >
      2
    ) {
      suggestions.push(
        'Consider replacing generic tags with more specific alternatives'
      );
    }

    return {
      matchScore,
      missing,
      extra,
      suggestions,
    };
  }
}

export default TagQualityValidator;
